import { useMutation, useQuery } from '@apollo/client';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import EmailFilingFormFields from './EmailFilingFormFields.tsx';
import Button from '../../../core/components/Button.tsx';
import ConfirmModal from '../../../core/components/ConfirmModal.tsx';
import { GET_NAVIGATION_CHANNELS } from '../../../core/components/Navigation/internal/queries.ts';
import SaveStatus from '../../../core/components/SaveStatus.tsx';
import { SettingsCard } from '../../../core/components/SettingsFormCard.tsx';
import Switch from '../../../core/components/Switch.tsx';
import { useToaster } from '../../../core/components/Toast.tsx';
import countFieldErrors from '../../../core/utils/countFieldErrors.ts';
import GeneralFormFields from '../../../customer/pages/SmartInbox/components/GeneralFormFields.tsx';
import ProductTypesFormFields from '../../../customer/pages/SmartInbox/components/ProductTypesFormFields.tsx';
import UnprocessedDocumentsFormFields from '../../../customer/pages/SmartInbox/components/UnprocessedDocumentsFormFields.tsx';
import {
    SMART_INBOX_FORM_DEFAULT_VALUES,
    createDefaultProductConfigs,
} from '../../../customer/pages/SmartInbox/SmartInboxConfigSteps.tsx';
import {
    SmartInboxIntegrationType,
    SmartInboxFormData,
    CustomCategoryActionKind,
    SUPPORTED_PRODUCT_TYPES,
} from '../../../customer/pages/SmartInbox/types.ts';
import { UPDATE_SMART_INBOX_INTEGRATION, DELETE_SMART_INBOX_INTEGRATION } from '../../../customer/queries.ts';
import { ConnectionAction, ConnectionStatus, DOCUMENT_TYPE } from '../../../document/constants.ts';
import { OVERVIEW_PATH } from '../constants.ts';

const cardTitle = (text: React.ReactNode) => <span className="text-base font-semibold">{text}</span>;
const cardDescription = (text: React.ReactNode) => <span className="text-sm">{text}</span>;

interface SettingsTabSharedProps {
    integration: SmartInboxIntegrationType;
    refetch: () => Promise<any>;
    onDisconnected: () => void;
}

const SettingsTabShared = ({ integration, refetch, onDisconnected }: SettingsTabSharedProps) => {
    const { t } = useTranslation(['mailboxes', 'integrations']);
    const navigate = useNavigate();
    const { publishToast } = useToaster();
    const [isDisconnecting, setIsDisconnecting] = useState(false);
    const [savingField, setSavingField] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [updateIntegration] = useMutation(UPDATE_SMART_INBOX_INTEGRATION);
    const [deleteIntegration, { loading: disconnecting }] = useMutation(DELETE_SMART_INBOX_INTEGRATION);
    const { data: channelData, loading: channelsLoading } = useQuery(GET_NAVIGATION_CHANNELS, {
        fetchPolicy: 'network-only',
        notifyOnNetworkStatusChange: false,
    });

    const currentStatus = integration.enabled ? ConnectionStatus.Connected : ConnectionStatus.Paused;

    const configuredProducts = (integration.productConfigs || []).filter((config: any) =>
        SUPPORTED_PRODUCT_TYPES.includes(config.documentType as DOCUMENT_TYPE)
    );
    const isSettingChannelsForFirstTime = configuredProducts.length === 0;

    const form = useForm<SmartInboxFormData>({ defaultValues: SMART_INBOX_FORM_DEFAULT_VALUES });

    useEffect(() => {
        if (!integration) return;

        const productConfigs = createDefaultProductConfigs();

        integration.productConfigs?.forEach((config: any) => {
            const productConfig = productConfigs[config.documentType];
            if (!productConfig) return;

            productConfig.channelId = config.channelId || null;

            config.actionConfigs?.forEach((actionConfig: any) => {
                const actionKey = `${actionConfig.eventKind}_${actionConfig.actionKind}`;
                const parsed = JSON.parse(actionConfig.actionConfig || '{}');

                productConfig.actionConfigs[actionKey] = {
                    id: actionConfig.id,
                    eventKind: actionConfig.eventKind,
                    actionKind: actionConfig.actionKind,
                    actionConfig:
                        actionConfig.actionKind === CustomCategoryActionKind.MARK_AS_READ
                            ? parsed[config.documentType] || false
                            : parsed,
                };
            });
        });

        form.reset({
            name: integration.name || '',
            tenantId: '',
            clientId: '',
            clientSecret: '',
            mailInbox: integration.mailInbox || '',
            enabled: integration.enabled ?? true,
            useSmartInboxAsCreatedBy: integration.useSmartInboxAsCreatedBy ?? true,
            isManualDocumentCreationEnabled: integration.isManualDocumentCreationEnabled ?? true,
            ignoreReplyAndForwardedEmails: integration.ignoreReplyAndForwardedEmails ?? false,
            productConfigs,
        });
    }, [integration]);

    const runUpdate = async (fieldKey: string, variables: Record<string, any>, successKey: string) => {
        setSavingField(fieldKey);
        try {
            await updateIntegration({
                variables: {
                    id: integration.id,
                    name: integration.name,
                    isManualDocumentCreationEnabled: integration.isManualDocumentCreationEnabled,
                    ignoreReplyAndForwardedEmails: integration.ignoreReplyAndForwardedEmails,
                    useSmartInboxAsCreatedBy: integration.useSmartInboxAsCreatedBy,
                    enabled: integration.enabled,
                    ...variables,
                },
            });
            await refetch();
            publishToast({ description: t(successKey), status: 'success' });
        } catch (error) {
            console.error(error);
            publishToast({ description: t('detail.settingsTab.saveError'), status: 'error' });
        } finally {
            setSavingField(null);
        }
    };

    const handleStatusAction = async (action: ConnectionAction) => {
        if (action === ConnectionAction.Disconnect) {
            setIsDisconnecting(true);
            return;
        }
        await runUpdate(
            'enabled',
            { enabled: action === ConnectionAction.Resume },
            action === ConnectionAction.Resume ? 'detail.settingsTab.resumeSuccess' : 'detail.settingsTab.pauseSuccess'
        );
    };

    const handleDisconnect = async () => {
        try {
            await deleteIntegration({ variables: { id: integration.id } });
            publishToast({ description: t('detail.settingsTab.disconnectSuccess'), status: 'success' });
            onDisconnected();
        } catch (error) {
            console.error(error);
            publishToast({ description: t('detail.settingsTab.saveError'), status: 'error' });
        } finally {
            setIsDisconnecting(false);
        }
    };

    const handleFormSubmit = async (formData: SmartInboxFormData) => {
        setIsSubmitting(true);
        try {
            const productConfigs = Object.entries(formData.productConfigs || {})
                .filter(([documentType, config]: [string, any]) => config.channelId || documentType === 'SMART_INBOX')
                .map(([documentType, config]: [string, any]) => ({
                    documentType,
                    channelId: config.channelId,
                    // only existing actions can be updated, and the API rejects an action without an id
                    actionConfigs: Object.values(config.actionConfigs || {})
                        .filter((actionConfig: any) => !!actionConfig.id)
                        .map((actionConfig: any) => ({
                            id: actionConfig.id,
                            eventKind: actionConfig.eventKind,
                            actionKind: actionConfig.actionKind,
                            actionConfig: JSON.stringify(
                                actionConfig.actionKind === CustomCategoryActionKind.MARK_AS_READ
                                    ? { [documentType]: actionConfig.actionConfig }
                                    : actionConfig.actionConfig || {}
                            ),
                        })),
                }));

            await updateIntegration({
                variables: {
                    id: integration.id,
                    name: formData.name,
                    isManualDocumentCreationEnabled: formData.isManualDocumentCreationEnabled,
                    ignoreReplyAndForwardedEmails: formData.ignoreReplyAndForwardedEmails,
                    useSmartInboxAsCreatedBy: formData.useSmartInboxAsCreatedBy,
                    enabled: integration.enabled,
                    ...(isSettingChannelsForFirstTime
                        ? {
                              channels: productConfigs
                                  .filter(
                                      ({ documentType, channelId }) =>
                                          !!channelId && SUPPORTED_PRODUCT_TYPES.includes(documentType as DOCUMENT_TYPE)
                                  )
                                  .map(({ documentType, channelId }) => ({ documentType, channelId })),
                              // setting up the channels rewrites the other actions in this save, so only send the keywords
                              productConfigs: productConfigs
                                  .filter(({ documentType }) => documentType === 'SMART_INBOX')
                                  .map((config) => ({
                                      ...config,
                                      actionConfigs: config.actionConfigs.filter(
                                          ({ actionKind }) => actionKind === 'POST_CLASSIFY'
                                      ),
                                  })),
                          }
                        : { productConfigs }),
                },
            });

            await refetch();
            publishToast({ description: t('detail.settingsTab.saveSuccess'), status: 'success' });
        } catch (error) {
            console.error(error);
            publishToast({ description: t('detail.settingsTab.saveError'), status: 'error' });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="flex flex-col gap-6">
            <form className="flex flex-col gap-6" onSubmit={form.handleSubmit(handleFormSubmit)}>
                <SettingsCard
                    className="max-w-none"
                    title={cardTitle(t('smartInbox.config.general.title', { ns: 'integrations' }))}
                    description={cardDescription(t('smartInbox.config.general.description', { ns: 'integrations' }))}
                >
                    <GeneralFormFields
                        form={form}
                        integration={integration}
                        isLoading={false}
                        isCreateMode={false}
                        hideEnabledField
                        hideRelocatedFields
                    />
                </SettingsCard>

                <SettingsCard
                    className="max-w-none"
                    title={cardTitle(t('smartInbox.config.products.title', { ns: 'integrations' }))}
                    description={cardDescription(t('smartInbox.config.products.description', { ns: 'integrations' }))}
                >
                    <ProductTypesFormFields
                        form={form}
                        integration={integration}
                        channels={channelData}
                        isLoading={channelsLoading}
                        isCreateMode={isSettingChannelsForFirstTime}
                    />
                </SettingsCard>

                <SettingsCard
                    className="max-w-none"
                    title={cardTitle(t('detail.settingsTab.skipAutomationTitle'))}
                    description={cardDescription(t('detail.settingsTab.skipAutomationDescription'))}
                >
                    <UnprocessedDocumentsFormFields
                        form={form}
                        destinationFolders={integration.destinationFolders || []}
                        isLoading={false}
                        showMoveToFolderField={false}
                    />

                    <Controller
                        name="ignoreReplyAndForwardedEmails"
                        control={form.control}
                        render={({ field }) => (
                            <label className="flex items-center justify-between gap-4 text-sm text-content-medium">
                                <span className="truncate">
                                    {t('smartInbox.form.ignoreReplyAndForwardedEmails', { ns: 'integrations' })}
                                </span>
                                <Switch value={!!field.value} onValueChange={field.onChange} />
                            </label>
                        )}
                    />
                </SettingsCard>

                <SettingsCard
                    className="max-w-none"
                    title={cardTitle(t('detail.settingsTab.documentsTitle'))}
                    description={cardDescription(t('detail.settingsTab.documentsDescription'))}
                >
                    <Controller
                        name="useSmartInboxAsCreatedBy"
                        control={form.control}
                        render={({ field }) => (
                            <label className="flex items-center justify-between gap-4 mt-4 text-sm text-content-medium">
                                <span className="truncate">
                                    {t('smartInbox.form.useSmartInboxAsCreatedBy', { ns: 'integrations' })}
                                </span>
                                <Switch value={!!field.value} onValueChange={field.onChange} />
                            </label>
                        )}
                    />

                    <Controller
                        name="isManualDocumentCreationEnabled"
                        control={form.control}
                        render={({ field }) => (
                            <label className="flex items-center justify-between gap-4 text-sm text-content-medium">
                                <span className="truncate">
                                    {t('smartInbox.form.isManualDocumentCreationEnabled', { ns: 'integrations' })}
                                </span>
                                <Switch value={!!field.value} onValueChange={field.onChange} />
                            </label>
                        )}
                    />

                    <UnprocessedDocumentsFormFields
                        form={form}
                        destinationFolders={integration.destinationFolders || []}
                        isLoading={false}
                        showKeywordFields={false}
                    />
                </SettingsCard>

                <SettingsCard
                    className="max-w-none"
                    title={cardTitle(t('detail.settingsTab.emailFiling.title'))}
                    description={cardDescription(t('detail.settingsTab.emailFiling.description'))}
                >
                    <EmailFilingFormFields
                        form={form}
                        destinationFolders={integration.destinationFolders || []}
                        productConfigs={integration.productConfigs}
                        isLoading={false}
                    />
                </SettingsCard>

                <SettingsCard
                    className="max-w-none"
                    title={cardTitle(t('detail.settingsTab.connectionTitle'))}
                    description={cardDescription(t('detail.settingsTab.connectionDescription'))}
                >
                    <div className="flex gap-3">
                        <Button
                            type="button"
                            variant="outline"
                            loading={savingField === 'enabled'}
                            className="!border-line-warning-default !text-content-warning-strong !font-semibold"
                            onClick={() =>
                                handleStatusAction(
                                    currentStatus === ConnectionStatus.Connected
                                        ? ConnectionAction.Pause
                                        : ConnectionAction.Resume
                                )
                            }
                        >
                            {t(
                                `detail.settingsTab.connectionAction.${
                                    currentStatus === ConnectionStatus.Connected ? 'pause' : 'resume'
                                }`
                            )}
                        </Button>
                        <Button
                            type="button"
                            variant="outline"
                            className="!border-line-danger-default !text-content-danger-strong font-semibold"
                            onClick={() => setIsDisconnecting(true)}
                        >
                            {t('detail.settingsTab.deactivate')}
                        </Button>
                    </div>
                </SettingsCard>

                <SaveStatus
                    isDirty={form.formState.isDirty}
                    isSubmitting={isSubmitting}
                    errorCount={countFieldErrors(form.formState.errors)}
                >
                    <Button type="button" variant="outline" onClick={() => navigate(OVERVIEW_PATH)}>
                        {t('detail.settingsTab.cancel')}
                    </Button>
                </SaveStatus>
            </form>

            {isDisconnecting && (
                <ConfirmModal
                    onConfirm={handleDisconnect}
                    onCancel={() => setIsDisconnecting(false)}
                    title={t('detail.settingsTab.disconnectConfirmTitle')}
                    labelConfirm={t('detail.settingsTab.disconnect')}
                    confirmDisabled={disconnecting}
                    danger
                >
                    {t('detail.settingsTab.disconnectConfirmMessage', { mailbox: integration.mailInbox })}
                </ConfirmModal>
            )}
        </div>
    );
};

export default SettingsTabShared;
